// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import {
	fetchStatus,
	setEnabled,
	uploadEvents,
	type VoiceDebugLoggingEvent,
} from '@app/features/voice/commands/VoiceDebugLoggingCommands';
import {SCREEN_SHARE_CODEC_NEGOTIATION_TOPIC} from '@app/features/voice/engine/ScreenShareCodecNegotiation';
import {asVoiceTrackSource, VoiceTrackSource} from '@app/features/voice/engine/VoiceTrackSource';
import {getNativeAudioCaptureDiagnosticState} from '@app/features/voice/utils/NativeAudioCaptureBridge';
import type {VoiceDebugLoggingStatusResponse} from '@fluxer/schema/src/domains/channel/ChannelSchemas';
import type {
	LocalTrackPublication,
	Participant,
	RemoteTrack,
	RemoteTrackPublication,
	Room,
	TrackPublication,
} from 'livekit-client';
import {RoomEvent} from 'livekit-client';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('VoiceDebugLoggingManager');

const DEFAULT_POLL_INTERVAL_MS = 10000;
const DEFAULT_UPLOAD_INTERVAL_MS = 2000;
const MAX_QUEUED_EVENTS = 1000;
const MAX_UPLOAD_BATCH_EVENTS = 200;
const SNAPSHOT_INTERVAL_MS = 10000;
const MAX_DEVICE_SNAPSHOT_AUDIO_TARGETS = 200;
const MAX_DEVICE_SNAPSHOT_PIPEWIRE_GRAPH_RECORDS = 500;
const TEXT_DECODER = new TextDecoder();

export type VoiceDebugLoggingSnapshotCollector = () =>
	| Record<string, unknown>
	| null
	| undefined
	| Promise<Record<string, unknown> | null | undefined>;

interface VoiceDebugLoggingStartOptions {
	guildId: string | null;
	channelId: string;
	connectionId: string | null;
	room: Room;
	collectSnapshot: VoiceDebugLoggingSnapshotCollector;
}

interface RoomParticipantSummary {
	identity: string;
	sid: string;
	isLocal: boolean;
	name?: string;
	metadata?: string;
	attributes?: Record<string, string>;
	connectionQuality?: string;
	isSpeaking?: boolean;
	permissions?: unknown;
	trackPublications: Array<TrackPublicationSummary>;
}

interface TrackPublicationSummary {
	trackSid: string;
	trackName?: string;
	source?: string;
	kind?: string;
	mimeType?: string;
	isMuted?: boolean;
	isSubscribed?: boolean;
	isEnabled?: boolean;
	dimensions?: {
		width: number;
		height: number;
	};
}

function millisecondsToNanosecondsString(milliseconds: number): string {
	if (!Number.isFinite(milliseconds) || milliseconds < 0) return '0';
	const wholeMs = Math.trunc(milliseconds);
	const fractionalNs = Math.round((milliseconds - wholeMs) * 1000000);
	return (BigInt(wholeMs) * 1000000n + BigInt(fractionalNs)).toString();
}

function createDiagnosticEvent(type: string, data?: Record<string, unknown>): VoiceDebugLoggingEvent {
	const monotonicNow = typeof performance !== 'undefined' ? performance.now() : Date.now();
	const timeOrigin =
		typeof performance !== 'undefined' && Number.isFinite(performance.timeOrigin)
			? performance.timeOrigin
			: Date.now() - monotonicNow;
	return {
		type,
		timestamp_ns: millisecondsToNanosecondsString(timeOrigin + monotonicNow),
		monotonic_ns: millisecondsToNanosecondsString(monotonicNow),
		...(data ? {data} : {}),
	};
}

function errorToData(error: unknown): Record<string, unknown> {
	if (error instanceof Error) {
		return {
			name: error.name,
			message: error.message,
			stack: error.stack,
		};
	}
	return {message: String(error)};
}

function getTrackDimensions(publication: TrackPublication): TrackPublicationSummary['dimensions'] | undefined {
	const track = publication.track;
	if (!track || !('dimensions' in track)) return undefined;
	const dimensions = (track as {dimensions?: {width?: number; height?: number}}).dimensions;
	if (typeof dimensions?.width !== 'number' || typeof dimensions.height !== 'number') return undefined;
	return {
		width: dimensions.width,
		height: dimensions.height,
	};
}

function summarizePublication(publication: TrackPublication): TrackPublicationSummary {
	return {
		trackSid: publication.trackSid,
		trackName: publication.trackName,
		source: publication.source,
		kind: publication.kind,
		mimeType: publication.mimeType,
		isMuted: publication.isMuted,
		isSubscribed: 'isSubscribed' in publication ? Boolean(publication.isSubscribed) : undefined,
		isEnabled: 'isEnabled' in publication ? Boolean(publication.isEnabled) : undefined,
		dimensions: getTrackDimensions(publication),
	};
}

function summarizeParticipant(participant: Participant | undefined): RoomParticipantSummary | null {
	if (!participant) return null;
	const trackPublications: Array<TrackPublicationSummary> = [];
	participant.trackPublications.forEach((publication) => {
		trackPublications.push(summarizePublication(publication));
	});
	return {
		identity: participant.identity,
		sid: participant.sid,
		isLocal: participant.isLocal,
		name: participant.name,
		metadata: participant.metadata,
		attributes: participant.attributes,
		connectionQuality: participant.connectionQuality,
		isSpeaking: participant.isSpeaking,
		permissions: participant.permissions,
		trackPublications,
	};
}

function summarizeRoom(room: Room | null): Record<string, unknown> | null {
	if (!room) return null;
	return {
		name: room.name,
		state: room.state,
		numParticipants: room.numParticipants,
		localParticipant: summarizeParticipant(room.localParticipant),
		remoteParticipants: Array.from(room.remoteParticipants.values()).map((participant) =>
			summarizeParticipant(participant),
		),
	};
}

function summarizeTrackEvent(
	publication: TrackPublication | RemoteTrackPublication | LocalTrackPublication,
	participant: Participant | undefined,
): Record<string, unknown> {
	return {
		publication: summarizePublication(publication as TrackPublication),
		participant: summarizeParticipant(participant),
		isScreenShare: asVoiceTrackSource(publication.source) === VoiceTrackSource.ScreenShare,
		isScreenShareAudio: asVoiceTrackSource(publication.source) === VoiceTrackSource.ScreenShareAudio,
	};
}

function summarizeRemoteTrack(track: RemoteTrack): Record<string, unknown> {
	return {
		sid: track.sid,
		kind: track.kind,
		source: track.source,
		mediaStreamTrackId: track.mediaStreamTrack?.id,
		readyState: track.mediaStreamTrack?.readyState,
		muted: track.mediaStreamTrack?.muted,
	};
}

function getRtpCapabilities(kind: 'audio' | 'video'): Record<string, unknown> {
	const sender =
		typeof RTCRtpSender !== 'undefined' && typeof RTCRtpSender.getCapabilities === 'function'
			? RTCRtpSender.getCapabilities(kind)
			: null;
	const receiver =
		typeof RTCRtpReceiver !== 'undefined' && typeof RTCRtpReceiver.getCapabilities === 'function'
			? RTCRtpReceiver.getCapabilities(kind)
			: null;
	return {
		sender,
		receiver,
	};
}

function limitAudioTargetSnapshot(value: unknown): unknown {
	if (!value || typeof value !== 'object' || !('targets' in value)) return value;
	const record = value as {targets?: unknown};
	if (!Array.isArray(record.targets)) return value;
	return {
		...value,
		targets: record.targets.slice(0, MAX_DEVICE_SNAPSHOT_AUDIO_TARGETS),
		targetCount: record.targets.length,
		truncated: record.targets.length > MAX_DEVICE_SNAPSHOT_AUDIO_TARGETS,
	};
}

function limitGraphArray(record: Record<string, unknown>, key: string): Record<string, unknown> {
	const value = record[key];
	if (!Array.isArray(value)) return record;
	return {
		...record,
		[key]: value.slice(0, MAX_DEVICE_SNAPSHOT_PIPEWIRE_GRAPH_RECORDS),
		[`${key}Count`]: value.length,
		[`${key}Truncated`]: value.length > MAX_DEVICE_SNAPSHOT_PIPEWIRE_GRAPH_RECORDS,
	};
}

function limitPipewireRoutingGraph(graph: unknown): unknown {
	if (!graph || typeof graph !== 'object') return graph;
	let limited = graph as Record<string, unknown>;
	limited = limitGraphArray(limited, 'nodes');
	limited = limitGraphArray(limited, 'ports');
	limited = limitGraphArray(limited, 'ownedLinks');
	return limited;
}

function limitPipewireRoutingGraphResult(result: unknown): unknown {
	if (!result || typeof result !== 'object') return result;
	const record = result as Record<string, unknown>;
	if ('graph' in record) {
		return {...record, graph: limitPipewireRoutingGraph(record.graph)};
	}
	if (Array.isArray(record.graphs)) {
		return {
			...record,
			graphs: record.graphs.map((entry) =>
				entry && typeof entry === 'object'
					? {...entry, graph: limitPipewireRoutingGraph((entry as Record<string, unknown>).graph)}
					: entry,
			),
		};
	}
	return result;
}

async function collectDeviceSnapshot(): Promise<Record<string, unknown>> {
	const electron = getElectronAPI();
	const mediaDevices =
		typeof navigator !== 'undefined' && navigator.mediaDevices?.enumerateDevices
			? await navigator.mediaDevices
					.enumerateDevices()
					.then((devices) =>
						devices.map((device) => ({
							deviceId: device.deviceId,
							groupId: device.groupId,
							kind: device.kind,
							label: device.label,
						})),
					)
					.catch((error) => ({error: errorToData(error)}))
			: null;
	const desktopInfo = electron?.getDesktopInfo
		? await electron.getDesktopInfo().catch((error) => ({error: errorToData(error)}))
		: null;
	const gpuInfo = electron?.getGpuInfo
		? await electron.getGpuInfo().catch((error) => ({error: errorToData(error)}))
		: null;
	const appMetrics = electron?.getAppMetrics
		? await electron.getAppMetrics().catch((error) => ({error: errorToData(error)}))
		: null;
	const nativeScreenCapture = electron?.nativeScreenCapture
		? await electron.nativeScreenCapture.getAvailability().catch((error) => ({error: errorToData(error)}))
		: null;
	const nativeAudio = electron?.nativeAudio
		? await Promise.all([
				electron.nativeAudio.getAvailability().catch((error) => ({error: errorToData(error)})),
				electron.nativeAudio.getRoutingGraph().catch((error) => ({error: errorToData(error)})),
			]).then(([availability, routingGraph]) => ({
				availability,
				pipewireRoutingGraph: limitPipewireRoutingGraphResult(routingGraph),
			}))
		: null;
	const virtmic = electron?.virtmic
		? await Promise.all([
				electron.virtmic.getAvailability().catch((error) => ({error: errorToData(error)})),
				electron.virtmic.listTargets({granular: true}).catch((error) => ({error: errorToData(error)})),
				electron.virtmic.getRoutingGraph().catch((error) => ({error: errorToData(error)})),
			]).then(([availability, targets, routingGraph]) => ({
				availability,
				targets: limitAudioTargetSnapshot(targets),
				pipewireRoutingGraph: limitPipewireRoutingGraphResult(routingGraph),
			}))
		: null;
	const nav = typeof navigator !== 'undefined' ? navigator : null;
	const screenSnapshot =
		typeof screen !== 'undefined'
			? {
					width: screen.width,
					height: screen.height,
					availWidth: screen.availWidth,
					availHeight: screen.availHeight,
					colorDepth: screen.colorDepth,
					pixelDepth: screen.pixelDepth,
					orientation: screen.orientation
						? {
								type: screen.orientation.type,
								angle: screen.orientation.angle,
							}
						: null,
				}
			: null;
	return {
		navigator: nav
			? {
					userAgent: nav.userAgent,
					platform: nav.platform,
					language: nav.language,
					languages: nav.languages,
					hardwareConcurrency: nav.hardwareConcurrency,
					deviceMemory: (nav as Navigator & {deviceMemory?: number}).deviceMemory,
					maxTouchPoints: nav.maxTouchPoints,
					cookieEnabled: nav.cookieEnabled,
					doNotTrack: nav.doNotTrack,
					onLine: nav.onLine,
				}
			: null,
		document:
			typeof document !== 'undefined'
				? {
						visibilityState: document.visibilityState,
						hidden: document.hidden,
					}
				: null,
		window:
			typeof window !== 'undefined'
				? {
						devicePixelRatio: window.devicePixelRatio,
						innerWidth: window.innerWidth,
						innerHeight: window.innerHeight,
						outerWidth: window.outerWidth,
						outerHeight: window.outerHeight,
					}
				: null,
		screen: screenSnapshot,
		mediaDevices,
		rtpCapabilities: {
			audio: getRtpCapabilities('audio'),
			video: getRtpCapabilities('video'),
		},
		desktop: {
			platform: electron?.platform ?? null,
			buildChannel: electron?.buildChannel ?? null,
			info: desktopInfo,
			gpuInfo,
			appMetrics,
			nativeScreenCapture,
			nativeAudio,
			nativeAudioCapture: getNativeAudioCaptureDiagnosticState(),
			virtmic,
		},
	};
}

function parseAllowedDataMessage(payload: Uint8Array, topic: string | undefined): Record<string, unknown> {
	if (topic !== SCREEN_SHARE_CODEC_NEGOTIATION_TOPIC) {
		return {
			topic: topic ?? null,
			payloadBytes: payload.byteLength,
			decoded: null,
		};
	}
	try {
		return {
			topic,
			payloadBytes: payload.byteLength,
			decoded: JSON.parse(TEXT_DECODER.decode(payload)) as unknown,
		};
	} catch (error) {
		return {
			topic,
			payloadBytes: payload.byteLength,
			decodeError: errorToData(error),
		};
	}
}

class VoiceDebugLoggingManager {
	active = false;
	sessionId: string | null = null;
	activatedByUserId: string | null = null;
	startedAtMs: number | null = null;
	expiresAtMs: number | null = null;
	pollIntervalMs = DEFAULT_POLL_INTERVAL_MS;
	uploadIntervalMs = DEFAULT_UPLOAD_INTERVAL_MS;
	toggleInFlight = false;
	lastError: string | null = null;
	private channelId: string | null = null;
	private connectionId: string | null = null;
	private participantIdentity: string | null = null;
	private room: Room | null = null;
	private collectSnapshot: VoiceDebugLoggingSnapshotCollector | null = null;
	private pollTimer: NodeJS.Timeout | null = null;
	private uploadTimer: NodeJS.Timeout | null = null;
	private roomDisposer: (() => void) | null = null;
	private queue: Array<VoiceDebugLoggingEvent> = [];
	private uploadInFlight = false;
	private generation = 0;
	private lastSnapshotAtMs = 0;

	constructor() {
		makeAutoObservable<
			this,
			| 'channelId'
			| 'connectionId'
			| 'participantIdentity'
			| 'room'
			| 'collectSnapshot'
			| 'pollTimer'
			| 'uploadTimer'
			| 'roomDisposer'
			| 'queue'
			| 'uploadInFlight'
			| 'generation'
			| 'lastSnapshotAtMs'
			| 'bindRoom'
			| 'clearTimers'
			| 'restartPollTimer'
			| 'restartUploadTimer'
			| 'refreshStatus'
			| 'applyStatus'
			| 'record'
			| 'enqueueDeviceSnapshot'
			| 'enqueueRuntimeSnapshot'
			| 'enqueuePeriodicSnapshotIfNeeded'
			| 'flush'
		>(
			this,
			{
				channelId: false,
				connectionId: false,
				participantIdentity: false,
				room: false,
				collectSnapshot: false,
				pollTimer: false,
				uploadTimer: false,
				roomDisposer: false,
				queue: false,
				uploadInFlight: false,
				generation: false,
				lastSnapshotAtMs: false,
				bindRoom: false,
				clearTimers: false,
				restartPollTimer: false,
				restartUploadTimer: false,
				refreshStatus: false,
				applyStatus: false,
				record: false,
				enqueueDeviceSnapshot: false,
				enqueueRuntimeSnapshot: false,
				enqueuePeriodicSnapshotIfNeeded: false,
				flush: false,
				start: false,
				stop: false,
				setEnabled: false,
			},
			{autoBind: true},
		);
	}

	get connectedChannelId(): string | null {
		return this.channelId;
	}

	async start(options: VoiceDebugLoggingStartOptions): Promise<void> {
		if (
			this.channelId === options.channelId &&
			this.connectionId === options.connectionId &&
			this.room === options.room
		) {
			return;
		}
		await this.stop('replaced');
		this.generation++;
		this.channelId = options.channelId;
		this.connectionId = options.connectionId;
		this.room = options.room;
		this.participantIdentity = options.room.localParticipant?.identity ?? null;
		this.collectSnapshot = options.collectSnapshot;
		this.bindRoom(options.room);
		this.record('voice.debug_logging.client_started', {
			guildId: options.guildId,
			channelId: options.channelId,
			connectionId: options.connectionId,
			room: summarizeRoom(options.room),
		});
		await this.refreshStatus('joined');
		this.restartPollTimer(this.pollIntervalMs);
	}

	async stop(reason = 'stopped'): Promise<void> {
		const wasStarted = this.channelId !== null;
		const wasActive = this.active;
		if (wasStarted) {
			this.record('voice.debug_logging.client_stopped', {
				reason,
				active: wasActive,
				channelId: this.channelId,
				connectionId: this.connectionId,
			});
			if (wasActive) {
				await this.flush('stop');
			}
		}
		this.generation++;
		this.clearTimers();
		this.roomDisposer?.();
		this.roomDisposer = null;
		this.queue = [];
		runInAction(() => {
			this.active = false;
			this.sessionId = null;
			this.activatedByUserId = null;
			this.startedAtMs = null;
			this.expiresAtMs = null;
			this.pollIntervalMs = DEFAULT_POLL_INTERVAL_MS;
			this.uploadIntervalMs = DEFAULT_UPLOAD_INTERVAL_MS;
			this.lastError = null;
		});
		this.channelId = null;
		this.connectionId = null;
		this.participantIdentity = null;
		this.room = null;
		this.collectSnapshot = null;
		this.lastSnapshotAtMs = 0;
	}

	async setEnabled(enabled: boolean): Promise<void> {
		const channelId = this.channelId;
		if (!channelId || this.toggleInFlight) return;
		runInAction(() => {
			this.toggleInFlight = true;
			this.lastError = null;
		});
		try {
			const status = await setEnabled(channelId, enabled);
			await this.applyStatus(status, enabled ? 'staff-enabled' : 'staff-disabled');
		} catch (error) {
			logger.warn('Failed to toggle voice debug logging session', {error, enabled, channelId});
			runInAction(() => {
				this.lastError = error instanceof Error ? error.message : String(error);
			});
		} finally {
			runInAction(() => {
				this.toggleInFlight = false;
			});
		}
	}

	private bindRoom(room: Room): void {
		this.roomDisposer?.();
		const onConnected = (): void => {
			this.record('livekit.room.connected', {room: summarizeRoom(room)});
		};
		const onDisconnected = (reason?: unknown): void => {
			this.record('livekit.room.disconnected', {reason: String(reason ?? 'unknown'), room: summarizeRoom(room)});
			void this.flush('room-disconnected');
		};
		const onReconnecting = (): void => {
			this.record('livekit.room.reconnecting', {room: summarizeRoom(room)});
		};
		const onReconnected = (): void => {
			this.record('livekit.room.reconnected', {room: summarizeRoom(room)});
			void this.enqueueRuntimeSnapshot('livekit.room.reconnected');
		};
		const onParticipantConnected = (participant: Participant): void => {
			this.record('livekit.participant.connected', {participant: summarizeParticipant(participant)});
		};
		const onParticipantDisconnected = (participant: Participant): void => {
			this.record('livekit.participant.disconnected', {participant: summarizeParticipant(participant)});
		};
		const onTrackPublished = (publication: RemoteTrackPublication, participant: Participant): void => {
			this.record('livekit.track.published', summarizeTrackEvent(publication, participant));
		};
		const onTrackUnpublished = (publication: RemoteTrackPublication, participant: Participant): void => {
			this.record('livekit.track.unpublished', summarizeTrackEvent(publication, participant));
		};
		const onTrackSubscribed = (
			track: RemoteTrack,
			publication: RemoteTrackPublication,
			participant: Participant,
		): void => {
			this.record('livekit.track.subscribed', {
				...summarizeTrackEvent(publication, participant),
				track: summarizeRemoteTrack(track),
			});
		};
		const onTrackUnsubscribed = (
			track: RemoteTrack,
			publication: RemoteTrackPublication,
			participant: Participant,
		): void => {
			this.record('livekit.track.unsubscribed', {
				...summarizeTrackEvent(publication, participant),
				track: summarizeRemoteTrack(track),
			});
		};
		const onTrackMuted = (publication: TrackPublication, participant: Participant): void => {
			this.record('livekit.track.muted', summarizeTrackEvent(publication, participant));
		};
		const onTrackUnmuted = (publication: TrackPublication, participant: Participant): void => {
			this.record('livekit.track.unmuted', summarizeTrackEvent(publication, participant));
		};
		const onLocalTrackPublished = (publication: LocalTrackPublication, participant: Participant): void => {
			this.record('livekit.local_track.published', summarizeTrackEvent(publication, participant));
		};
		const onLocalTrackUnpublished = (publication: LocalTrackPublication, participant: Participant): void => {
			this.record('livekit.local_track.unpublished', summarizeTrackEvent(publication, participant));
		};
		const onActiveSpeakersChanged = (speakers: Array<Participant>): void => {
			this.record('livekit.active_speakers.changed', {
				speakers: speakers.map((participant) => summarizeParticipant(participant)),
			});
		};
		const onDataReceived = (
			payload: Uint8Array,
			participant: Participant | undefined,
			kind: unknown,
			topic?: string,
		): void => {
			this.record('livekit.data.received', {
				participant: summarizeParticipant(participant),
				kind: String(kind),
				...parseAllowedDataMessage(payload, topic),
			});
		};
		room.on(RoomEvent.Connected, onConnected);
		room.on(RoomEvent.Disconnected, onDisconnected);
		room.on(RoomEvent.Reconnecting, onReconnecting);
		room.on(RoomEvent.Reconnected, onReconnected);
		room.on(RoomEvent.ParticipantConnected, onParticipantConnected);
		room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
		room.on(RoomEvent.TrackPublished, onTrackPublished);
		room.on(RoomEvent.TrackUnpublished, onTrackUnpublished);
		room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
		room.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
		room.on(RoomEvent.TrackMuted, onTrackMuted);
		room.on(RoomEvent.TrackUnmuted, onTrackUnmuted);
		room.on(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
		room.on(RoomEvent.LocalTrackUnpublished, onLocalTrackUnpublished);
		room.on(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged);
		room.on(RoomEvent.DataReceived, onDataReceived);
		this.roomDisposer = () => {
			room.off(RoomEvent.Connected, onConnected);
			room.off(RoomEvent.Disconnected, onDisconnected);
			room.off(RoomEvent.Reconnecting, onReconnecting);
			room.off(RoomEvent.Reconnected, onReconnected);
			room.off(RoomEvent.ParticipantConnected, onParticipantConnected);
			room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
			room.off(RoomEvent.TrackPublished, onTrackPublished);
			room.off(RoomEvent.TrackUnpublished, onTrackUnpublished);
			room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
			room.off(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
			room.off(RoomEvent.TrackMuted, onTrackMuted);
			room.off(RoomEvent.TrackUnmuted, onTrackUnmuted);
			room.off(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
			room.off(RoomEvent.LocalTrackUnpublished, onLocalTrackUnpublished);
			room.off(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged);
			room.off(RoomEvent.DataReceived, onDataReceived);
		};
	}

	private clearTimers(): void {
		if (this.pollTimer) {
			clearInterval(this.pollTimer);
			this.pollTimer = null;
		}
		if (this.uploadTimer) {
			clearInterval(this.uploadTimer);
			this.uploadTimer = null;
		}
	}

	private restartPollTimer(intervalMs: number): void {
		if (!this.channelId) return;
		if (this.pollTimer) {
			clearInterval(this.pollTimer);
		}
		const generation = this.generation;
		this.pollTimer = setInterval(() => {
			if (generation !== this.generation) return;
			void this.refreshStatus('poll');
		}, intervalMs);
	}

	private restartUploadTimer(intervalMs: number): void {
		if (this.uploadTimer) {
			clearInterval(this.uploadTimer);
			this.uploadTimer = null;
		}
		if (!this.active) return;
		const generation = this.generation;
		this.uploadTimer = setInterval(() => {
			if (generation !== this.generation) return;
			void this.flush('interval');
		}, intervalMs);
	}

	private async refreshStatus(reason: string): Promise<void> {
		const channelId = this.channelId;
		if (!channelId) return;
		try {
			const status = await fetchStatus(channelId);
			await this.applyStatus(status, reason);
		} catch (error) {
			logger.debug('Failed to refresh voice debug logging status', {error, channelId, reason});
			runInAction(() => {
				this.lastError = error instanceof Error ? error.message : String(error);
			});
		}
	}

	private async applyStatus(status: VoiceDebugLoggingStatusResponse, reason: string): Promise<void> {
		const previousActive = this.active;
		const previousSessionId = this.sessionId;
		const becameActive =
			status.active && status.session_id !== null && (!previousActive || previousSessionId !== status.session_id);
		const becameInactive = previousActive && (!status.active || status.session_id === null);
		runInAction(() => {
			this.active = status.active && status.session_id !== null;
			this.sessionId = status.session_id;
			this.activatedByUserId = status.activated_by_user_id;
			this.startedAtMs = status.started_at_ms;
			this.expiresAtMs = status.expires_at_ms;
			this.pollIntervalMs = status.poll_interval_ms || DEFAULT_POLL_INTERVAL_MS;
			this.uploadIntervalMs = status.upload_interval_ms || DEFAULT_UPLOAD_INTERVAL_MS;
			this.lastError = null;
		});
		this.restartPollTimer(this.pollIntervalMs);
		this.restartUploadTimer(this.uploadIntervalMs);
		if (becameActive) {
			this.record('voice.debug_logging.session_active', {
				reason,
				sessionId: status.session_id,
				activatedByUserId: status.activated_by_user_id,
				startedAtMs: status.started_at_ms,
				expiresAtMs: status.expires_at_ms,
			});
			await this.enqueueDeviceSnapshot();
			await this.enqueueRuntimeSnapshot('session_active');
			void this.flush('session-active');
		} else if (becameInactive) {
			this.record('voice.debug_logging.session_inactive', {reason, previousSessionId});
			this.queue = [];
		}
	}

	private record(type: string, data?: Record<string, unknown>): void {
		this.queue.push(createDiagnosticEvent(type, data));
		if (this.queue.length > MAX_QUEUED_EVENTS) {
			this.queue.splice(0, this.queue.length - MAX_QUEUED_EVENTS);
		}
		if (this.active && this.queue.length >= MAX_UPLOAD_BATCH_EVENTS) {
			void this.flush('queue-full');
		}
	}

	private async enqueueDeviceSnapshot(): Promise<void> {
		try {
			this.record('voice.debug_logging.device_snapshot', await collectDeviceSnapshot());
		} catch (error) {
			this.record('voice.debug_logging.device_snapshot_failed', errorToData(error));
		}
	}

	private async enqueueRuntimeSnapshot(reason: string): Promise<void> {
		const collector = this.collectSnapshot;
		if (!collector) return;
		try {
			this.record('voice.debug_logging.runtime_snapshot', {
				reason,
				room: summarizeRoom(this.room),
				snapshot: (await collector()) ?? null,
			});
			this.lastSnapshotAtMs = Date.now();
		} catch (error) {
			this.record('voice.debug_logging.runtime_snapshot_failed', {reason, ...errorToData(error)});
		}
	}

	private async enqueuePeriodicSnapshotIfNeeded(reason: string): Promise<void> {
		if (!this.active || Date.now() - this.lastSnapshotAtMs < SNAPSHOT_INTERVAL_MS) return;
		await this.enqueueRuntimeSnapshot(reason);
	}

	private async flush(reason: string): Promise<void> {
		if (this.uploadInFlight) return;
		const channelId = this.channelId;
		const sessionId = this.sessionId;
		if (!this.active || !channelId || !sessionId) return;
		await this.enqueuePeriodicSnapshotIfNeeded(reason);
		const events = this.queue.splice(0, MAX_UPLOAD_BATCH_EVENTS);
		if (events.length === 0) return;
		this.uploadInFlight = true;
		try {
			const response = await uploadEvents({
				channelId,
				sessionId,
				connectionId: this.connectionId,
				participantIdentity: this.participantIdentity,
				events,
			});
			if (!response.accepted || !response.active) {
				await this.refreshStatus('upload-rejected');
			}
		} catch (error) {
			logger.debug('Failed to upload voice debug logging events', {error, channelId, reason, count: events.length});
			this.queue = [...events, ...this.queue].slice(0, MAX_QUEUED_EVENTS);
			runInAction(() => {
				this.lastError = error instanceof Error ? error.message : String(error);
			});
		} finally {
			this.uploadInFlight = false;
		}
	}
}

export default new VoiceDebugLoggingManager();
